import React from 'react';
import { CheckCircle, Bell, Mail, MessageSquare, Calendar, ArrowRight } from 'lucide-react';
import Button from '../utils/Button';

interface ConfirmationSuccessProps {
  engagementData: any;
  onFinish: () => void;
}

export default function ConfirmationSuccess({ engagementData, onFinish }: ConfirmationSuccessProps) {
  const hasReminders = engagementData.emailReminders || engagementData.textReminders;
  const hasPledge = engagementData.voterPledge;

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full mb-6 animate-pulse">
          <CheckCircle className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-2">
          You're All Set 
        </h1>
        <p className="text-xl text-gray-600">Your preferences have been saved.</p>
      </div>

      {/* Summary Section */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-2xl p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
          What Happens Next
        </h2>
        
        <div className="space-y-4">
          {/* Reminders Section */}
          {hasReminders && (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <Bell className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">You'll receive reminders based on your selections</h3>
                <div className="flex flex-wrap gap-2 mt-2">
                  {engagementData.emailReminders && (
                    <span className="inline-flex items-center px-3 py-1 bg-white border border-blue-200 rounded-full text-sm">
                      <Mail className="w-3 h-3 mr-1 text-blue-600" />
                      Email
                    </span>
                  )}
                  {engagementData.textReminders && (
                    <span className="inline-flex items-center px-3 py-1 bg-white border border-blue-200 rounded-full text-sm">
                      <MessageSquare className="w-3 h-3 mr-1 text-blue-600" />
                      SMS
                    </span>
                  )}
                  {engagementData.reminder7Days && (
                    <span className="inline-flex items-center px-3 py-1 bg-white border border-purple-200 rounded-full text-sm">
                      <Calendar className="w-3 h-3 mr-1 text-purple-600" />
                      7 Days
                    </span>
                  )}
                  {engagementData.reminder1Day && (
                    <span className="inline-flex items-center px-3 py-1 bg-white border border-purple-200 rounded-full text-sm">
                      <Calendar className="w-3 h-3 mr-1 text-purple-600" />
                      1 Day
                    </span>
                  )}
                  {engagementData.reminderElectionDay && (
                    <span className="inline-flex items-center px-3 py-1 bg-white border border-purple-200 rounded-full text-sm">
                      <Calendar className="w-3 h-3 mr-1 text-purple-600" />
                      Election Day
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Pledge Section */}
          {hasPledge && (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Your personal voting pledge has been saved</h3>
                <p className="text-sm text-gray-600 mt-1">This commitment can help motivate you to participate in upcoming elections.</p>
              </div>
            </div>
          )}

          {/* Unsubscribe Info */}
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
              <ArrowRight className="w-4 h-4 text-gray-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">You can unsubscribe at any time</h3>
              <p className="text-sm text-gray-600 mt-1">No further action is required right now.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Success Features */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="text-center p-4 bg-green-50 rounded-xl border border-green-200">
          <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center mx-auto mb-2">
            <CheckCircle className="w-6 h-6 text-green-700" />
          </div>
          <h3 className="font-medium text-gray-900 text-sm">Preferences Saved</h3>
          <p className="text-xs text-gray-600 mt-1">Your choices are stored securely</p>
        </div>
        
        <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-200">
          <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center mx-auto mb-2">
            <Bell className="w-6 h-6 text-blue-700" />
          </div>
          <h3 className="font-medium text-gray-900 text-sm">Reminders Active</h3>
          <p className="text-xs text-gray-600 mt-1">You'll be notified when needed</p>
        </div>
        
        <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-200">
          <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center mx-auto mb-2">
            <Calendar className="w-6 h-6 text-purple-700" />
          </div>
          <h3 className="font-medium text-gray-900 text-sm">Ready to Vote</h3>
          <p className="text-xs text-gray-600 mt-1">All set for upcoming elections</p>
        </div>
      </div>

      {/* Compliance Note */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-8">
        <p className="text-xs text-gray-600 text-center leading-relaxed">
          123Vote is a non-partisan civic engagement platform.
          <br />
          We do not endorse or oppose candidates, parties, or ballot measures.
          <br />
          Your information is used solely for civic engagement purposes.
        </p>
      </div>

      {/* Finish Button */}
      <Button
        label="Finish"
        variant="primary"
        size="extra-large"
        onClick={onFinish}
        icon={<ArrowRight className="w-5 h-5" />}
        iconPosition="right"
        additionalClassname="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
      />
    </div>
  );
}
